import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { AuthShell } from "./AuthShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function Activate() {
  const { activate, resendActivation } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({ status: "loading", message: "" });
  const [email, setEmail] = useState("");
  const [resendMsg, setResendMsg] = useState("");
  const [resending, setResending] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState({ status: "error", message: "Missing activation token." });
      return;
    }
    activate(token).then((res) => {
      if (res.ok) setState({ status: "success", message: res.message });
      else setState({ status: "error", message: res.error });
    });
  }, [token, activate]);

  const doResend = async () => {
    if (!email) return;
    setResending(true);
    const res = await resendActivation(email);
    setResending(false);
    setResendMsg(res.ok ? (res.message || "A new activation link has been sent.") : (res.error || "Could not resend."));
  };

  return (
    <AuthShell>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center" data-testid="activate-card">
        {state.status === "loading" && (
          <>
            <Loader2 className="h-12 w-12 text-[#1E3A8A] mx-auto animate-spin" />
            <h2 className="font-heading text-2xl font-bold text-slate-900 mt-4">Activating your account…</h2>
          </>
        )}
        {state.status === "success" && (
          <div data-testid="activate-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <h2 className="font-heading text-2xl font-bold text-slate-900 mt-4">Account activated</h2>
            <p className="text-sm text-slate-500 mt-2">{state.message}</p>
            <Button asChild className="mt-6 bg-[#1E3A8A] hover:bg-[#1D4ED8]">
              <Link to="/login" data-testid="activate-signin">Sign In</Link>
            </Button>
          </div>
        )}
        {state.status === "error" && (
          <div data-testid="activate-error">
            <XCircle className="h-12 w-12 text-red-500 mx-auto" />
            <h2 className="font-heading text-2xl font-bold text-slate-900 mt-4">Activation link invalid or expired</h2>
            <p className="text-sm text-slate-500 mt-2">{state.message}</p>

            <div className="mt-6 text-left border-t border-slate-100 pt-5">
              <Label htmlFor="resend-email" className="text-sm">Request a new activation link</Label>
              <div className="flex gap-2 mt-1.5">
                <Input id="resend-email" data-testid="activate-resend-email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@silastech.com" />
                <Button data-testid="activate-resend-btn" onClick={doResend} disabled={!email || resending}
                  className="bg-[#1E3A8A] hover:bg-[#1D4ED8] shrink-0">
                  {resending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Send link
                </Button>
              </div>
              {resendMsg && <p className="text-sm text-emerald-700 mt-2" data-testid="activate-resend-msg">{resendMsg}</p>}
            </div>

            <Button asChild variant="outline" className="mt-6">
              <Link to="/login">Back to Sign In</Link>
            </Button>
          </div>
        )}
      </div>
    </AuthShell>
  );
}
